import { Component, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ControlValueAccessorComponent } from '../../core';
import { LibCoreLayoutModule } from '../core-layout';
import { sameValueValidator, validatorAgain } from '../../validators';

@Component({
  selector: 'lib-password-form',
  templateUrl: './password-form.component.html',
  styleUrls: ['./password-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    ReactiveFormsModule,
    // Material design
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFormComponent),
      multi: true,
    },
  ],
})
export class PasswordFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public readonly FORM_NAME = {
    PASSWORD: 'password',
    PASSWORD_CONFIRM: 'passwordConfirm',
  };

  public submitForm: FormGroup;

  public hidePassword = true;

  private readonly PASSWORD_MIN_LENGTH = 6;

  constructor(private translateService: TranslateService) {
    super();
    this.submitForm = new FormGroup({
      [this.FORM_NAME.PASSWORD]: new FormControl(null, [
        Validators.required,
        Validators.minLength(this.PASSWORD_MIN_LENGTH),
        validatorAgain(this.FORM_NAME.PASSWORD_CONFIRM),
      ]),
      [this.FORM_NAME.PASSWORD_CONFIRM]: new FormControl(null, [
        Validators.required,
        sameValueValidator(
          this.FORM_NAME.PASSWORD,
          this.translateService.instant('password-form.text.password'),
          this.translateService.instant(
            'password-form.text.password-confirmation'
          )
        ),
      ]),
    });

    this.submitForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        if (this.submitForm.valid) {
          this._onChange(this.controlPassword.value);
        } else {
          this._onChange(null);
        }
      });
  }

  public get controlPassword(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PASSWORD)!;
  }

  public get controlPasswordConfirm(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PASSWORD_CONFIRM)!;
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(password: string) {
    this.controlPassword.setValue(password);
    this.controlPasswordConfirm.setValue(password);
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.submitForm.disable();
    } else {
      this.submitForm.enable();
    }
  }
}
